<template>
  <div v-if="flight.legs?.length" class="summarized" @click="expand">
    <div class="head">
      <div class="dt">
        {{ utilDateFlightfoxUTC(flight.legs[0].time_depart) }}
        <span v-if="utilDateFlightfoxUTC(flight.legs[0].time_depart) != utilDateFlightfoxUTC(flight.legs[flight.legs.length - 1].time_arrive)"> - {{ utilDateFlightfoxUTC(flight.legs[flight.legs.length - 1].time_arrive) }} </span>
        <a v-if="isSummary && isEdit" @click.stop="emit('edit')">Change</a>
      </div>
    </div>
    <div class="common">
      <div class="collogo">
        <div class="logos" :class="{ multi: airlinesCodes(flight).length > 1 }">
          <img v-for="(logo, index) in airlinesLogos(flight)" :key="index" :src="logo" />
        </div>
      </div>
      <div class="others">
        <div class="colother">
          <div class="pri times">
            <span>{{ utilTime(flight.legs[0].time_depart) }} - {{ utilTime(flight.legs[flight.legs.length - 1].time_arrive) }}</span>
            <span class="days">{{ utilDurationFlightOvernights(flight.legs[0].time_depart, flight.legs[flight.legs.length - 1].time_arrive, true) }}</span>
          </div>
          <div class="sec">{{ airlinesTitles(flight) }}</div>
        </div>
        <div class="colother">
          <div class="pri times max">{{ utilDurationItineraryMinutes(flight.elapsed) }}</div>
          <div class="sec max">{{ airportsRoute(flight) }}</div>
        </div>
        <div class="colother">
          <div class="pri max">{{ stops(flight) }}</div>
          <div class="sec max">{{ airportsStops(flight) }}</div>
        </div>
        <div v-if="!isSummary" class="colother">
          <div v-if="!isFinished && !flight.price_display?.amount" class="priceload">
            <div class="loading"></div>
          </div>
          <div v-else>
            <div class="pri">
              <div class="price">
                <div v-if="!flight.price_display?.amount">TBA</div>
                <div v-else>
                  <div class="amt">
                    <!-- flight.price_display.amount is canonical USD; formatPrice renders org-currency display. -->
                    <span>{{ priceLabel(flight.price_display.amount) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="sec">{{ priceTypeLabel() }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="extra">
      <div class="tags">
        <span v-if="isLander && showFlightType && flight.is_group_flight === true" class="tag chaperoned">{{ groupFlightLabel }}</span>
        <span v-else-if="isLander && showFlightType && flight.is_group_flight === false" class="tag">{{ individualFlightLabel }}</span>
        <span v-if="isLander && hasDisclaimers" class="tag notrec">Important</span>
        <span v-if="!isLander && isFinished && flight.price_display.is_policy && flight.price_display.amount > 0" class="tag policy" :class="{ within: flight.price_display.is_policy_within }">{{ flight.is_policy }}{{ policyLabel(flight.price_display.is_policy_within) }}</span>
        <span v-if="flight.is_cheapest" class="tag highlight">Cheapest</span>
        <span v-if="flight.is_best" class="tag highlight">Best</span>
      </div>
      <span class="icon" :class="{ open: isDetails }">&#x2c5;</span>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import imageUrl from "../../assets/images/WN.png";
import DateTime from "../../utils/datetime";
import { formatPrice } from "../../utils/money";

const { utilTime, utilDateFlightfoxUTC, utilDurationFlightOvernights, utilDurationItineraryMinutes } = DateTime();

const props = defineProps(["flight", "flightWay", "isDetails", "isFinished", "isLander", "showFlightType", "way", "isEdit"]);
const emit = defineEmits(["update:is-details", "selectFlight", "scroll", "edit"]);

const groupFlightLabel = inject("groupFlightLabel", ref("Chaperoned"));
const individualFlightLabel = inject("individualFlightLabel", ref("Unchaperoned"));

const airlinesCodes = flight => {
  const codes = [];
  if (!flight.legs) return codes;
  flight.legs.forEach(leg => {
    const code = leg.is_codeshare ? leg.airline_iata_op : leg.airline_iata;
    if (codes.indexOf(code) == -1) {
      codes.push(code);
    }
  });
  return codes;
};

const hasDisclaimers = computed(() => {
  return props.flight.disclaimers?.length > 0;
});

const isSummary = computed(() => {
  return props.flightWay == "summary";
});

const airlinesLogo = code => {
  if (code == "WN") {
    return imageUrl;
  }
  return "https://www.gstatic.com/flights/airline_logos/70px/" + code + ".png";
};

const airlinesLogos = flight => {
  const logos = [];
  const codes = airlinesCodes(flight);
  codes.forEach(code => {
    logos.push(airlinesLogo(code));
  });
  return logos;
};

const airlinesTitles = flight => {
  const names = [];
  if (!flight.legs) return "";
  flight.legs.forEach(leg => {
    const title = leg.is_codeshare ? leg.airline_title_op : leg.airline_title;
    if (names.indexOf(title) == -1) {
      names.push(title);
    }
  });
  return names.join(", ");
};

const airportsRoute = flight => {
  if (!flight.legs?.length) return "";
  const route = [];
  route.push(flight.legs[0].airport_depart_iata);
  route.push(flight.legs[flight.legs.length - 1].airport_arrive_iata);
  return route.join("-");
};

const airportsStops = flight => {
  if (!flight.legs?.length) return "Direct";
  const route = [];
  for (let i = 0; i < flight.legs.length; i++) {
    if (i > 0) {
      route.push(flight.legs[i].airport_depart_iata);
    }
    if (flight.legs[i].stops > 0) {
      route.push("XXX");
    }
  }
  return route.length > 0 ? route.join(", ") : "Direct";
};

const expand = () => {
  emit("update:is-details", !props.isDetails);
};

const stops = flight => {
  if (!flight.legs?.length) return "Nonstop";
  const stops = flight.legs.length - 1;
  return stops == 0 ? "Nonstop" : stops == 1 ? "1 stop" : stops + " stops";
};

const policyLabel = is => (is ? "In Policy" : "Out of Policy");

// Input is canonical USD; formatPrice consults org currency captured at search time.
const priceLabel = p => formatPrice(p);

const priceTypeLabel = () => {
  if (props.flightWay == "summary") return "";
  return props.flightWay == "oneway" ? "One way" : props.flightWay == "roundtrip" ? "Round trip" : "Multi city";
};
</script>

<style lang="less">
#flights {
  .dt {
    flex: 1;
    a {
      float: right;
    }
  }

  .tags {
    flex: 1;

    .tag {
      background: @white;
      border: 0.0625rem solid @border;
      display: inline-block;
      border-radius: 0.5rem;
      color: @mid;
      font-size: 0.75rem;
      margin-right: 0.5rem;
      margin-bottom: 0.25rem;
      padding: 0.125rem 0.5rem;
      text-align: center;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .chaperoned {
      background-color: @lightblue;
      border-color: @primary;
      color: @primary;
    }

    .highlight {
      background-color: @lightgreen;
      border-color: @green;
      color: @green;
    }

    .notrec,
    .policy {
      background-color: @lightred;
      border-color: @secondary;
      color: @secondary;
    }

    .policy.within {
      background-color: @lightblue;
      border-color: @green;
      color: @green;
    }
  }

  .summarized {
    cursor: pointer;
    padding: 1rem;

    .max {
      display: none;
    }

    .head,
    .extra,
    .common,
    .others {
      display: flex;
      flex: 1;
    }

    .head {
      border-bottom: 0.0625rem solid @border;
      margin-bottom: 1rem;
      padding-bottom: 1rem;
    }

    .collogo {
      display: none;
    }

    .colother {
      flex: 1;
      min-width: 0;
      text-align: right;

      &:first-child {
        text-align: left;
      }

      .pri {
        margin-bottom: 0.25rem;
      }

      .sec {
        color: @mid;
        font-size: 0.875rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .row {
        display: flex;

        > div {
          flex: 1;
        }

        > div:first-child {
          text-align: left;
        }
      }

      .times {
        white-space: nowrap;

        .days {
          font-size: 0.75rem;
          vertical-align: super;
        }
      }

    }

    .price {
      .amt {
        .sym {
          padding-right: 0.125rem;
        }
      }
    }

    .extra {
      border-top: 0.0625rem solid @border;
      display: flex;
      margin-top: 1rem;
      padding-top: 1rem;

      .icon {
        margin-left: 1rem;
        transition: transform 0.25s;
      }

      .icon.open {
        transform: rotate(180deg);
      }
    }

    .priceload {
      align-items: center;
      display: flex;
      height: 100%;
      float: right;
    }
  }
}

@media screen and (min-width: 60em) {
  #flights {
    .summarized {
      .max {
        display: block;
      }

      .collogo {
        display: flex;
        margin-right: 2rem;

        .logos {
          align-self: center;
          height: 2.75rem;
          position: relative;
          width: 2.75rem;

          img {
            display: block;
            width: 2.75rem;
          }

          &.multi {
            img {
              position: absolute;
              width: 1.25rem;
            }

            img:nth-child(1) {
              left: 0;
              top: 0;
            }

            img:nth-child(2) {
              bottom: 0;
              right: 0;
            }

            img:nth-child(3) {
              bottom: 0;
              left: 0;
            }

            img:nth-child(4) {
              right: 0;
              top: 0;
            }
          }
        }
      }
    }
  }
}
</style>
